import React, { useState, useEffect } from 'react';
import { Panel } from 'reactflow';
import { useTheme } from '../../contexts/ThemeContext';
import { useTintedBackground } from '../../hooks/useTintedBackground';
import { WorkflowValidation } from './utils/workflowValidation';
import * as personaService from '../../services/personaService';
import * as toolService from '../../services/toolService';

interface ValidationPanelProps {
  validation: WorkflowValidation;
  show: boolean;
  onFocusNode?: (nodeId: string) => void;
  onAutoFix?: (error: any) => void;
  mode?: string; // Add mode prop to know if we're in edit mode
}

export const ValidationPanel: React.FC<ValidationPanelProps> = ({ 
  validation, 
  show, 
  onFocusNode,
  onAutoFix,
  mode
}) => {
  const { darkMode } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  
  // State for personas and tools for auto-fix
  const [availablePersonas, setAvailablePersonas] = useState<any[]>([]);
  const [availableTools, setAvailableTools] = useState<any[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(false);
  
  // Just use a fixed margin that clears the Builder panel
  const getTopMargin = () => {
    return '320px'; // Fixed margin that clears the Builder panel
  };

  // Add tinted background effect
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? '#ef4444' : '#dc2626', // Red tint for validation issues
    enabled: true
  });

  // Function to load personas and tools for auto-fix
  const loadPersonasAndToolsForAutoFix = async () => {
    if (availablePersonas.length > 0 && availableTools.length > 0) {
      console.log('🔧 AUTO-FIX: Data already loaded, skipping reload');
      return; // Already loaded
    }
    
    setIsLoadingData(true);
    try {
      console.log('🔧 AUTO-FIX: Loading personas and tools for auto-fix...');
      
      const [personas, tools] = await Promise.all([
        personaService.getPersonas(),
        toolService.getTools()
      ]);
      
      console.log('🔧 AUTO-FIX: Raw data loaded - personas:', personas.length, 'tools:', tools.length);
      
      // Format personas (same logic as ChatWithCanvas)
      const formattedPersonas = personas.map(persona => ({
        name: persona.name,
        id: persona.id, // Use ONLY the actual ID
        tags: (persona.tags || []).slice(0, 10),
        specialization: (persona.shortDescription || persona.primarySpecialization || 'General purpose').substring(0, 50)
      }));
      
      // Format tools (same logic as ChatWithCanvas)
      const formattedTools = tools.map(tool => ({
        name: tool.name,
        id: tool.name,
        category: tool.metadata?.category || 'general',
        description: (tool.metadata?.documentation?.summary || 'No description').substring(0, 50),
        tags: (tool.metadata?.tags || []).slice(0, 5),
        version: tool.metadata?.version ? 
          `${tool.metadata.version.major}.${tool.metadata.version.minor}.${tool.metadata.version.patch}` : 
          '1.0.0',
        permission_level: tool.metadata?.permission_level || 'basic'
      }));
      
      setAvailablePersonas(formattedPersonas);
      setAvailableTools(formattedTools);
      
      console.log('🔧 AUTO-FIX: Formatted and stored', formattedPersonas.length, 'personas and', formattedTools.length, 'tools');
      console.log('🔧 AUTO-FIX: Sample personas:', formattedPersonas.slice(0, 3).map(p => p.name));
      console.log('🔧 AUTO-FIX: Sample tools:', formattedTools.slice(0, 3).map(t => t.name));
      
    } catch (error) {
      console.error('🔧 AUTO-FIX: Failed to load personas and tools:', error);
      // Set empty arrays as fallback
      setAvailablePersonas([]);
      setAvailableTools([]);
    } finally {
      setIsLoadingData(false);
    }
  };

  if (!show) return null;

  const { errors, warnings, isValid } = validation;
  const totalIssues = errors.length + warnings.length;

  if (totalIssues === 0 && isValid) {
    return (
      <Panel 
        position="top-left" 
        style={{ 
          marginTop: getTopMargin(),
          marginLeft: '20px' // Align with Builder panel
        }}
      >
        <div
          ref={tintedBg.ref}
          className={`p-3 rounded-xl shadow-xl border max-w-sm transition-all duration-300 ${
            darkMode 
              ? 'border-green-700/50' 
              : 'border-green-200/50'
          }`}
          style={{
            ...tintedBg.style,
            backgroundColor: darkMode ? 'rgba(5, 46, 22, 0.1)' : 'rgba(240, 253, 244, 0.1)',
          }}
        >
          {/* Dynamic zoom overlay following cursor */}
          <div
            className="absolute inset-0 pointer-events-none rounded-xl"
            style={{
              ...tintedBg.overlayStyle,
              background: `radial-gradient(circle at ${tintedBg.mousePosition.x}% ${tintedBg.mousePosition.y}%, ${
                darkMode ? 'rgba(34, 197, 94, 0.08)' : 'rgba(22, 163, 74, 0.08)'
              } 0%, transparent 60%)`,
            }}
          />
          
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm font-medium">Workflow Valid ✅</span>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel 
      position="top-left"
      style={{ 
        marginTop: getTopMargin(),
        marginLeft: '20px' // Align with Builder panel
      }}
    >
      <div
        ref={tintedBg.ref}
        className={`rounded-xl shadow-xl border max-w-md transition-all duration-300 ${
          darkMode 
            ? 'border-red-700/50' 
            : 'border-red-200/50'
        } ${collapsed ? 'max-w-xs' : 'max-w-md'}`} // Responsive width based on collapse state
        style={{
          ...tintedBg.style,
          backgroundColor: darkMode ? 'rgba(69, 10, 10, 0.1)' : 'rgba(254, 242, 242, 0.1)',
        }}
      >
        {/* Dynamic zoom overlay following cursor */}
        <div
          className="absolute inset-0 pointer-events-none rounded-xl"
          style={tintedBg.overlayStyle}
        />

        {/* Header - always visible with better collapse indicator */}
        <div 
          className={`flex items-center justify-between p-3 cursor-pointer transition-all duration-200 ${
            darkMode ? 'hover:bg-gray-700/30' : 'hover:bg-gray-50/30'
          }`}
          onClick={() => setCollapsed(!collapsed)}
        >
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full animate-pulse ${
              errors.length > 0 ? 'bg-red-500' : 'bg-yellow-500'
            }`}></div>
            <span className="text-sm font-medium">
              {collapsed ? 'Issues' : 'Validation Issues'} ({totalIssues})
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {/* Quick status indicators when collapsed */}
            {collapsed && (
              <div className="flex space-x-1">
                {errors.length > 0 && (
                  <span className="text-xs px-1.5 py-0.5 bg-red-500 text-white rounded-full">
                    {errors.length}
                  </span>
                )}
                {warnings.length > 0 && (
                  <span className="text-xs px-1.5 py-0.5 bg-yellow-500 text-white rounded-full">
                    {warnings.length}
                  </span>
                )}
              </div>
            )}
            <button className={`text-sm transition-transform duration-200 ${
              collapsed ? 'rotate-180' : ''
            }`}>
              ▼
            </button>
          </div>
        </div>

        {/* Content with smooth expand/collapse animation */}
        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
          collapsed ? 'max-h-0' : 'max-h-96'
        }`}>
          <div className="max-h-80 overflow-y-auto">
          {/* Errors */}
          {errors.map((error, index) => (
            <div 
              key={`error-${index}`}
              className={`p-3 border-l-4 border-red-500 ${
                darkMode ? 'bg-red-900/20' : 'bg-red-50'
              } ${index > 0 ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-red-600 text-xs font-bold">ERROR</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      error.severity === 'high' ? 'bg-red-600 text-white' :
                      error.severity === 'medium' ? 'bg-orange-500 text-white' :
                      'bg-yellow-500 text-black'
                    }`}>
                      {error.severity}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed">{error.message}</p>
                </div>
              
                <div className="flex space-x-1 ml-2 flex-shrink-0">
                  {error.nodeId && onFocusNode && (
                    <button
                      onClick={() => onFocusNode(error.nodeId)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      📍
                    </button>
                  )}
                  {onAutoFix && (
                    <button
                      onClick={async () => {
                        console.log('🔧 AUTO-FIX: Processing error:', error);
                        
                        // Show loading state
                        setIsLoadingData(true);
                        
                        try {
                          // Load personas and tools first
                          await loadPersonasAndToolsForAutoFix();
                          
                          // Generate fix instruction with available data
                          let fixInstruction = '';
                          
                          if (error.errorType === 'no_personas_assigned' || error.message.includes('no personas assigned')) {
                            const phaseName = error.phaseName || 'the phase';
                            
                            // Send ALL personas, let LLM choose
                            if (availablePersonas.length > 0) {
                              fixInstruction = `Add appropriate personas to the "${phaseName}" phase while preserving all existing nodes, connections, and phase configurations. Do not modify any other phases or remove any existing elements. Choose the best personas from the available list.`;
                            } else {
                              fixInstruction = `Add appropriate personas to the "${phaseName}" phase while preserving all existing workflow structure. Loading available personas...`;
                            }
                          } else if (error.errorType === 'no_tools_assigned' || error.message.includes('no tools assigned')) {
                            const phaseName = error.phaseName || 'the phase';
                            
                            // Send ALL tools, let LLM choose
                            if (availableTools.length > 0) {
                              fixInstruction = `Add appropriate tools to the "${phaseName}" phase while preserving all existing nodes, connections, and phase configurations. Do not modify any other phases or remove any existing elements. Choose the best tools from the available list.`;
                            } else {
                              fixInstruction = `Add appropriate tools to the "${phaseName}" phase while preserving all existing workflow structure. Loading available tools...`;
                            }
                          } else if (error.errorType === 'disconnected_phase' || error.message.includes('not connected to the workflow flow')) {
                            const phaseName = error.phaseName || 'the phase';
                            fixInstruction = `Connect ${phaseName} to the workflow by adding appropriate artifact flow connections to other phases.`;
                          } else if (error.errorType === 'missing_connections' || error.message.includes('missing connections')) {
                            fixInstruction = `Add missing connections between phases to ensure proper workflow flow and persona assignments.`;
                          } else if (error.errorType === 'circular_dependency' || error.message.includes('circular dependencies')) {
                            fixInstruction = `Fix the circular dependency in the workflow by removing or redirecting problematic connections.`;
                          } else {
                            fixInstruction = `Fix the validation issue: ${error.message}`;
                          }
                          
                          console.log('🔧 AUTO-FIX: Generated instruction with loaded data:', fixInstruction);
                          console.log('🔧 AUTO-FIX: Available personas count:', availablePersonas.length);
                          console.log('🔧 AUTO-FIX: Available tools count:', availableTools.length);
                          
                          // Call the original onAutoFix with enhanced error object
                          onAutoFix({
                            ...error,
                            fixInstruction,
                            availablePersonas,
                            availableTools
                          });
                          
                        } catch (autoFixError) {
                          console.error('🔧 AUTO-FIX: Error during auto-fix:', autoFixError);
                          // Fallback to original behavior
                          onAutoFix(error);
                        } finally {
                          setIsLoadingData(false);
                        }
                      }}
                      disabled={isLoadingData}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        isLoadingData 
                          ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                          : 'bg-red-600 hover:bg-red-700 text-white'
                      }`}
                    >
                      {isLoadingData ? '...' : 'Fix'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Warnings */}
          {warnings.map((warning, index) => (
            <div 
              key={`warning-${index}`}
              className={`p-3 border-l-4 border-yellow-500 ${
                darkMode ? 'bg-yellow-900/20' : 'bg-yellow-50'
              } ${(index > 0 || errors.length > 0) ? 'border-t border-gray-200 dark:border-gray-700' : ''}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-yellow-600 text-xs font-bold">WARNING</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      warning.severity === 'high' ? 'bg-red-600 text-white' :
                      warning.severity === 'medium' ? 'bg-orange-500 text-white' :
                      'bg-yellow-500 text-black'
                    }`}>
                      {warning.severity}
                    </span>
                  </div>
                  <p className="text-sm leading-relaxed">{warning.message}</p>
                </div>
              
                <div className="flex space-x-1 ml-2 flex-shrink-0">
                  {warning.nodeId && onFocusNode && (
                    <button
                      onClick={() => onFocusNode(warning.nodeId)}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        darkMode 
                          ? 'bg-gray-700 hover:bg-gray-600 text-white' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                    >
                      📍
                    </button>
                  )}
                  {onAutoFix && (
                    <button
                      onClick={async () => {
                        console.log('🔧 AUTO-FIX: Processing warning:', warning);
                        
                        // Show loading state
                        setIsLoadingData(true);
                        
                        try {
                          // Load personas and tools first
                          await loadPersonasAndToolsForAutoFix();
                          
                          // Generate fix instruction with available data
                          let fixInstruction = '';
                          
                          if (warning.errorType === 'no_personas_assigned' || warning.message.includes('no personas assigned')) {
                            const phaseName = warning.phaseName || 'the phase';
                            
                            // Send ALL personas, let LLM choose
                            if (availablePersonas.length > 0) {
                              fixInstruction = `Add appropriate personas to the "${phaseName}" phase while preserving all existing nodes, connections, and phase configurations. Do not modify any other phases or remove any existing elements. Choose the best personas from the available list.`;
                            } else {
                              fixInstruction = `Add appropriate personas to the "${phaseName}" phase while preserving all existing workflow structure. Loading available personas...`;
                            }
                          } else if (warning.errorType === 'no_tools_assigned' || warning.message.includes('no tools assigned')) {
                            const phaseName = warning.phaseName || 'the phase';
                            
                            // Send ALL tools, let LLM choose
                            if (availableTools.length > 0) {
                              fixInstruction = `Add appropriate tools to the "${phaseName}" phase while preserving all existing nodes, connections, and phase configurations. Do not modify any other phases or remove any existing elements. Choose the best tools from the available list.`;
                            } else {
                              fixInstruction = `Add appropriate tools to the "${phaseName}" phase while preserving all existing workflow structure. Loading available tools...`;
                            }
                          } else if (warning.errorType === 'disconnected_phase' || warning.message.includes('not connected to the workflow flow')) {
                            const phaseName = warning.phaseName || 'the phase';
                            fixInstruction = `Connect ${phaseName} to the workflow by adding appropriate artifact flow connections to other phases.`;
                          } else if (warning.errorType === 'missing_connections' || warning.message.includes('missing connections')) {
                            fixInstruction = `Add missing connections between phases to ensure proper workflow flow and persona assignments.`;
                          } else {
                            fixInstruction = `Fix the validation issue: ${warning.message}`;
                          }
                          
                          console.log('🔧 AUTO-FIX: Generated warning instruction with loaded data:', fixInstruction);
                          console.log('🔧 AUTO-FIX: Available personas count:', availablePersonas.length);
                          console.log('🔧 AUTO-FIX: Available tools count:', availableTools.length);
                          
                          // Call the original onAutoFix with enhanced warning object
                          onAutoFix({
                            ...warning,
                            fixInstruction,
                            availablePersonas,
                            availableTools
                          });
                          
                        } catch (autoFixError) {
                          console.error('🔧 AUTO-FIX: Error during warning auto-fix:', autoFixError);
                          // Fallback to original behavior
                          onAutoFix(warning);
                        } finally {
                          setIsLoadingData(false);
                        }
                      }}
                      disabled={isLoadingData}
                      className={`px-2 py-1 text-xs rounded transition-colors ${
                        isLoadingData 
                          ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                          : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                      }`}
                    >
                      {isLoadingData ? '...' : 'Fix'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
          </div>
        </div>
      </div>
    </Panel>
  );
};
